import moment from 'moment';
import { Dispatch } from 'redux';
import { RootState } from 'store';

import {
  Expense,
  ExpenseForm,
  TravelExpensesResponse,
  ExpenseTravels,
  TravelsWithExpensesResponse,
  TravelsWithExpensesFilters,
  RappiExpensesByCountryResponse,
  RappiExpensesFilters,
} from 'models/expense';
import { RappiOrdersResponse } from 'models/rappiOrdersResponse';
import { Response } from 'models/response';
import { getFormData } from 'utils/generic';

import { travel, axiosConfig, rappiServices } from '../api';

export const saveExpense = (expense: ExpenseForm, currentExpense?: Expense) => (
  _: Dispatch,
  getState: () => RootState,
) => {
  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };

  if (currentExpense) {
    if (!expense.fileUpdated) {
      delete expense.file;
      delete expense.fileExtension;
      delete currentExpense.file;
      delete currentExpense.fileExtension;
    }

    return travel.put(
      `/Expenses/${currentExpense.id}`,
      getFormData({
        ...currentExpense,
        ...expense,
        date: moment(expense.date).format('YYYY-MM-DD HH:mm:ss'),
      }),
      config,
    );
  }
  return travel.post<Expense>(
    '/Expenses/',
    getFormData({
      ...expense,
      currencyCode: getState().user.accountResult?.countryCurrency,
      date: moment(expense.date).format('YYYY-MM-DD HH:mm:ss'),
    }),
    config,
  );
};

export const importRappiExpense = (travelId: string, expenses: Expense[]): Promise<void> =>
  travel.post(
    '/Expenses/ImportRappi',
    {
      travelId,
      rappiExpenses: expenses,
    },
    axiosConfig,
  );

export const deleteExpense = (expenseId: string): Promise<void> => travel.delete(`/Expenses/${expenseId}`, axiosConfig);

export const fetchExpense = (expenseId: string) =>
  travel.get<Response<Expense>>(`/Expenses/getExpenseById/${expenseId}`, axiosConfig);

export const fetchTravelExpenses = (travelId: string) =>
  travel.get<Response<TravelExpensesResponse>>(`/Expenses/GetExpensesByTravelId/${travelId}`, axiosConfig);

export const fetchAllTravelWithExpensesByUser = (filters: TravelsWithExpensesFilters) => {
  const startDateFilter = filters?.FilterStartDate ? `&FilterStartDate=${filters.FilterStartDate}` : '';
  const endDateFilter = filters?.FilterEndDate ? `&FilterEndDate=${filters.FilterEndDate}` : '';

  return travel.get<Response<TravelsWithExpensesResponse>>(
    `/Expenses/GetAllTravelsWithExpensesByUser?${startDateFilter}${endDateFilter}`,
    axiosConfig,
  );
};

export const fetchTravelExpensesRappi = (travelId: string) =>
  travel.get<Response<Expense[]>>(`/Expenses/GetRappiExpenses/${travelId}`, axiosConfig);

export const fetchAllConfirmedTravelsByUser = () =>
  travel.get<Response<ExpenseTravels[]>>('/Expenses/GetAllConfirmedTravelsByUser/', axiosConfig);

export const generateReport = (travelId: string) => travel.get(`/Expenses/Export/${travelId}`, axiosConfig);

export const downloadReport = (travelId: string) =>
  travel.get(`/Expenses/Download/${travelId}`, {
    ...axiosConfig,
    headers: { Accept: '*', 'Content-Type': '' },
    responseType: 'blob',
  });

export const getRappiExpenses = (rappiId: string, page: number) =>
  rappiServices.get<RappiOrdersResponse>(`ms/order-history/application-user/${rappiId}/page/${page}?`);

export const getRappiExpensesByCountry = (rappiId: string, filters: RappiExpensesFilters) => {
  const params = { rappiId, ...filters };
  return travel.get<Response<RappiExpensesByCountryResponse>>('/Expenses/TravelExpensesByCountry', {
    params,
    ...axiosConfig,
  });
};
